/* eslint-disable camelcase */
import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { initialize } from 'redux-form';
import styled from 'styled-components';

import useEffectOnce from '../../../js/hooks/useEffectOnce';
import stripNonNumeric from '../../../js/utils/strip-non-numeric';

import FormBlock from '../../blocks/FormBlock';
import withOptions from '../../utils/withOptions';
import withTwoPassRendering from '../../utils/withTwoPassRendering';
import Seo from '../Seo';
import ImageBlock from '../../blocks/ImageBlock';
import LinkBlock from '../../blocks/LinkBlock';
import SeoMetaProps from '../../../models/SeoMeta';
import iframeHeightObserver from '../../../js/utils/iframe-height-observer';
import partnerFormPostMessageListener from '../../../js/utils/partner-form-post-message-listener';
import isClient from '../../../js/utils/is-client';
import devices from '../../../js/styles/Devices';
import replaceACFVariables from '../../../libs/utils/replace-acf-variables';

const Form = styled.div`
  && {
    padding: 0.75rem;
    max-width: 100% !important;
  }
`;

const Title = styled.h2`
  && {
    font-size: 2.5rem;
    text-align: center;
    margin-top: 0;
  }
`;

const Wrapper = styled.div`
  && {
    border: 1px solid var(--lighter-grey);
    border-radius: 5px;
    overflow: hidden;

    @media ${devices.upFromTablet} {
      display: flex;
    }
  }
`;

const ImageWrapper = styled.div`
  && {
    display: block;
    flex: 0 1 50%;
    background: var(--lighter-grey);
  }
`;

const PartnerImage = styled.img`
  && {
    display: block;
    width: 100%;
  }
`;

const PartnerFormStyle = styled.div`
  && {
    flex: 1;
    background: var(--lightest-grey);

    @media ${devices.upFromTablet} {
      flex: 0 0 50%;
    }
  }
`;

const PartnerFooter = styled.div`
  && {
    padding: 1rem;
  }
`;

const PartnerFormLink = styled(LinkBlock)`
  && {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--darken-light-grey-40);
    text-decoration: none;

    @media ${devices.upFromTablet} {
      flex-direction: row;
      align-items: flex-end;
      order: 2;
      margin-left: 2rem;
    }
  }
`;

const PartnerLogo = styled(ImageBlock)`
  && {
    height: 2.2rem;
    margin-top: 0.25rem;
    @media ${devices.upFromTablet} {
      margin-left: 0.5rem;
      margin-top: 0;
      overflow: hidden;
    }
  }
`;

const PartnerDisclaimer = styled.div`
  && {
    flex: 1;
    font-size: 0.8rem;
    margin-top: 1rem;
    text-align: center;

    @media ${devices.upFromTablet} {
      order: 1;
      margin-top: 0;
      text-align: left;
    }
  }
`;

const Template = {
  FORM: 'form',
  FORM_WITH_IMAGE: 'form-with-image',
};

let pendingEvents = [];
const catchPendingMessageEvents = (event) => {
  if (!event.data) {
    return;
  }

  pendingEvents.push(event);
};

if (isClient) {
  window.addEventListener('message', catchPendingMessageEvents, false);
}

const PartnerForm = ({
  data: {
    wpPartnerForm: {
      templatePartnerForm,
      seo,
    },
  },
  disclaimer,
  exampleLoan,
  location,
  value,
}) => {
  const ref = useRef();
  const queryParams = queryString.parse(location.search);
  const application = useSelector((state) => state.form.application);
  const { values: formValues = {} } = application;
  const reinitializeForm = initialize;

  const postMessageListener = useCallback((event) => {
    partnerFormPostMessageListener(event, formValues, reinitializeForm);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isClient) {
      window.removeEventListener('message', catchPendingMessageEvents, false);
      window.addEventListener('message', postMessageListener, false);

      if (pendingEvents.length) {
        pendingEvents.forEach((event) => {
          postMessageListener(event);
        });
        pendingEvents = [];
      }

      return () => {
        window.removeEventListener('message', postMessageListener, false);
      };
    }

    return undefined;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  let path = '';
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    path = url.pathname.replace(/\/$/, '');
  }

  useEffectOnce(() => {
    if (path !== '/partners/bilweb-auto') {
      iframeHeightObserver({
        observer: {
          subtree: true,
          childList: true,
          attributes: true,
        },
        ref: ref.current,
      });
    }
  });

  const {
    utm_source,
    utm_medium,
    utm_campaign,
  } = templatePartnerForm.tracking;

  const utmTags = (utm_source || utm_medium || utm_campaign)
    ? `?utm_source=${utm_source || ''}&`
    + `utm_medium=${utm_medium || ''}&`
    + `utm_campaign=${utm_campaign || ''}`
    : '';

  const baseURL = process.env.GATSBY_FRONTEND_BASE_URL;
  const linkURL = templatePartnerForm.link?.link?.url;
  const absoluteLinkRegex = /^(http|https):\/\//;

  // eslint-disable-next-line no-nested-ternary
  const fullLinkURL = linkURL
    ? linkURL.match(absoluteLinkRegex)
      ? linkURL.replace(/\/*$/, '')
      : `${baseURL}${linkURL}`.replace(/\/*$/, '')
    : null;

  const downPaymentFactor = 1 - ((templatePartnerForm.downPayment || 0) / 100);

  let prefilledValues;
  if (queryParams.objCost) {
    prefilledValues = {
      ...prefilledValues,
      totalAmount: String(
        Number(stripNonNumeric(queryParams.objCost)) * downPaymentFactor,
      ),
    };
  }

  if (path === '/partners/bilweb-auto') {
    return (

      <Form>
        <>
          <Seo
            seo={seo}
          />
          {templatePartnerForm?.title
        && <Title>{replaceACFVariables(templatePartnerForm.title, value.optionVariables)}</Title>}
          <Wrapper>
            {templatePartnerForm.template[0] === Template.FORM_WITH_IMAGE && (
            <ImageWrapper>
              <PartnerImage
                src={queryParams.objImg}
                alt=""
              />
            </ImageWrapper>
            )}
            <PartnerFormStyle>
              <FormBlock
                {...templatePartnerForm.form}
                prefilledValues={prefilledValues}
                disclaimer={null}
                fullWidth
                elevated={false}
                rounded={false}
                forwardRef={ref}
                exampleLoan={exampleLoan}
                partnerForm
              />
            </PartnerFormStyle>
          </Wrapper>
          {(fullLinkURL || disclaimer) && (
          <PartnerFooter>
            {fullLinkURL && (
            <PartnerFormLink
              link={{
                ...templatePartnerForm.link.link,
                url: `${fullLinkURL}/${utmTags}`,
              }}
            >
              {templatePartnerForm.logo && (
              <PartnerLogo
                imageObject={templatePartnerForm.logo}
              />
              )}
            </PartnerFormLink>
            )}
            <PartnerDisclaimer>{disclaimer}</PartnerDisclaimer>
          </PartnerFooter>
          )}
        </>
      </Form>
    );
  }

  return (

    <Form>
      <>
        <Seo
          seo={seo}
        />
        {templatePartnerForm?.title
        && <Title>{replaceACFVariables(templatePartnerForm.title, value.optionVariables)}</Title>}
        <Wrapper>
          {templatePartnerForm.template[0] === Template.FORM_WITH_IMAGE && (
          <ImageWrapper>
            <PartnerImage
              src={queryParams.objImg}
              alt=""
            />
          </ImageWrapper>
          )}
          <PartnerFormStyle>
            <FormBlock
              {...templatePartnerForm.form}
              prefilledValues={prefilledValues}
              disclaimer={null}
              fullWidth
              elevated={false}
              rounded={false}
              forwardRef={ref}
              exampleLoan={exampleLoan}
              partnerForm
            />
          </PartnerFormStyle>
        </Wrapper>
        {(fullLinkURL || disclaimer) && (
          <PartnerFooter>
            {fullLinkURL && (
              <PartnerFormLink
                link={{
                  ...templatePartnerForm.link.link,
                  url: `${fullLinkURL}/${utmTags}`,
                }}
              >
                {templatePartnerForm.logo && (
                  <PartnerLogo
                    imageObject={templatePartnerForm.logo}
                  />
                )}
              </PartnerFormLink>
            )}
            <PartnerDisclaimer>{disclaimer}</PartnerDisclaimer>
          </PartnerFooter>
        )}
      </>
    </Form>
  );
};

PartnerForm.propTypes = {
  application: PropTypes.shape(),
  data: PropTypes.shape({
    wpPartnerForm: PropTypes.shape({
      id: PropTypes.string,
      templatePartnerForm: PropTypes.shape({
        template: PropTypes.string,
        title: PropTypes.string,
        form: PropTypes.shape(FormBlock.propTypes),
        backgroundColor: PropTypes.string,
        secondaryBackgroundColor: PropTypes.string,
        themeColor: PropTypes.string,
        accentColor: PropTypes.string,
        tracking: PropTypes.shape({
          utm_source: PropTypes.string,
          utm_medium: PropTypes.string,
          utm_campaign: PropTypes.string,
        }),
        link: PropTypes.shape(LinkBlock.propTypes),
        logo: ImageBlock.propTypes.imageObject,
        downPayment: PropTypes.number,
      }),
      seo: SeoMetaProps,
    }),
    wordpressAcfOptions: PropTypes.shape(),
    siteMeta: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
    }),
  }),
  reinitializeForm: PropTypes.func,
  disclaimer: PropTypes.string,
  exampleLoan: PropTypes.shape(),
  uiStore: PropTypes.any, //eslint-disable-line
  location: PropTypes.shape().isRequired,
  value: PropTypes.shape(),
};

PartnerForm.defaultProps = {
  application: {},
  data: {},
  reinitializeForm: () => {},
  disclaimer: null,
  exampleLoan: null,
  value: {},
};

export default (
  withTwoPassRendering(withOptions(PartnerForm))
);

export const query = graphql`
  query ($id: String!) {
    wpPartnerForm(id: {eq: $id}) {
      id
      templatePartnerForm {
        template
        title
        form {
          ...formBlockFields
        }
        backgroundColor
        secondaryBackgroundColor
        themeColor
        accentColor
        tracking {
          utm_source
          utm_medium
          utm_campaign
        }
        link {
          ...linkBlockFields
        }
        logo {
          ...imageBlockFieldsWithoutImage
          image {
            node {
              gatsbyImage(width: 500, quality: 100, layout: CONSTRAINED)
            }
          }
        }
        downPayment
      }
      seo {
        ...seoExtendedFields
      }
    }
  }
`;
